import { useCallback, useContext } from "react";

import { FeatureFlagValue, FormType, RecaptchaGuardMessage, TicketingFormCategory } from "@simplyk/common";
import { useSnackbar } from "notistack";
import { FieldValues, UseFormSetValue } from "react-hook-form";

import { AmplitudeEvents } from "../../../../common/constants/amplitude";
import { RecaptchaAction } from "../../../../common/constants/recaptcha";
import { FrontendFormContext } from "../../../../common/contexts/FrontendFormContext";
import { useUserInputContext } from "../../../../common/contexts/UserInputContext";
import { trpc } from "../../../../common/helpers/trpc";
import { useAmplitude } from "../../../../common/hooks/amplitude/useAmplitude";
import { useFormQueryParams } from "../../../../common/hooks/useFormParams";
import { useRecaptchaRetry } from "../../../../common/hooks/useRecaptchaRetry";
import { useUpsertSession } from "../../../../common/hooks/useUpsertSession";
import { formatSubmitCommandData, SubmitCommandFormInput } from "../helper";
import { PaymentElementProps, PaymentSucceeded } from "../type";

// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { unknownPaymentErrorCode } from "@/canvas/features/FormV2/blocks/payment/PaymentSection/PaymentElement/helper";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { useFormV2Context, useTicketingFormV2ContextOrDefaults } from "@/canvas/features/FormV2/contexts/FormV2Context";
import { getDeclineCodeFromServerError } from "@/common/helpers/stripe";
import { useTranslate } from "@/common/hooks/useTranslate";
import { TicketingPaymentInput } from "@/common/types/ticketing";

export interface PaymentFailed {
  error: { message: string; code: string; declineCode?: string };
}

export type SubmitCommandResponse = PaymentSucceeded | PaymentFailed;

export const useSubmitCommand = <T extends FieldValues>({ setValue }: Pick<PaymentElementProps<T>, "setValue">) => {
  const { mutateAsync: submitCommandMutation } = trpc.form_submitCommand.useMutation();
  const { mutateAsync: logCommandUserInteractionMutation } = trpc.form_logCommandUserInteraction.useMutation();

  const {
    displayedFormAmount,
    stripeRecurrenceInterval,
    commandId,
    organization,
    category,
    tipExperiment,
    paymentStepExperiment,
    isSuggestedTipsAmount,
    suggestedTips,
    defaultTipSuggestion,
  } = useContext(FrontendFormContext);

  const { upsertSession } = useUpsertSession({
    displayedFormAmount,
    tipSuggestions: suggestedTips,
    defaultTipSuggestion,
    isSuggestedTipsAmount,
    tipExperimentValue: tipExperiment ? tipExperiment.value : null,
  });

  const formV2Context = useFormV2Context();

  const { userInputs } = useUserInputContext();
  const { purchaseAttribution } = useFormQueryParams();

  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const { executeTrpc } = useRecaptchaRetry();
  const {
    returnToSelectionOnFullTicketing: fallBackToTicketingContainerOnFullTicketing,
    returnToSelectionOnNoTicketSelected: fallBackToTicketingContainerOnNoTicketSelected,
  } = useTicketingFormV2ContextOrDefaults();
  const { logAmplitudeEvent } = useAmplitude();

  const handleSubmitCommand = async (data: SubmitCommandFormInput): Promise<SubmitCommandResponse> => {
    if (!organization) {
      return { error: { message: t("common", "unknown_error"), code: "NoOrganizationError" } };
    }

    const params = formatSubmitCommandData(data, { displayedFormAmount, stripeRecurrenceInterval });
    const action = RecaptchaAction.SubmitCommand;
    const email = params.email;

    if (params.productBids) {
      await logCommandUserInteractionMutation({ commandId, payload: userInputs });
    }

    let suggestedAmountsExperiment: typeof params.suggestedAmountsExperiment = undefined;

    if (formV2Context.type === FormType.DonationForm) {
      const {
        hasChangedDonationAmount,
        suggestedAmountsExperimentData,
        lastInteractionWasClickedAmount,
        lastClickedAmountWasIndex,
      } = formV2Context;
      if (!hasChangedDonationAmount) {
        logAmplitudeEvent(AmplitudeEvents.DefaultAmountUsed);
      }

      // Add experiment data if available
      if (suggestedAmountsExperimentData) {
        suggestedAmountsExperiment = {
          ...suggestedAmountsExperimentData,
          lastInteractionWasClickedAmount,
          lastClickedAmountWasIndex,
        };
      }
    }

    const experiments = [tipExperiment, paymentStepExperiment]
      .filter((experiment): experiment is { name: string; value: FeatureFlagValue } => experiment !== null)
      .map((experiment) => ({ name: experiment.name, value: experiment.value }));

    const tipExperimentsPayload =
      experiments.length > 0
        ? {
            experiments,
            suggestedTipAmounts: suggestedTips?.map(({ amount }) => amount),
            suggestedTipPercentages: suggestedTips?.map(({ percentage }) => percentage),
            defaultTipSuggestionAmount: defaultTipSuggestion?.amount,
            defaultTipSuggestionPercentage: defaultTipSuggestion?.percentage,
            isSuggestedTipsAmount,
            userAgent: typeof window !== "undefined" ? window.navigator.userAgent : undefined,
          }
        : undefined;

    const experimentValues = {
      ...(tipExperimentsPayload && { tipExperiments: tipExperimentsPayload }),
    };

    // do not await this, not blocking
    void upsertSession();
    const response = await executeTrpc(action, email, submitCommandMutation, {
      ...params,
      id: commandId,
      ...purchaseAttribution,
      experimentValues,
      suggestedAmountsExperiment,
    });
    const responseData = response.data;
    const error = response.error;

    if (error?.message === RecaptchaGuardMessage) {
      return { error: { message: t("common", "recaptcha_error"), code: "RecaptchaFailed" } };
    }

    if (error || !responseData) {
      const displayedMessage = getErrorDisplayMessage({ error, t });
      handleFallbackOnPaymentError(error);
      return {
        error: {
          code: error?.code || "",
          message: displayedMessage,
          declineCode: getDeclineCodeFromServerError(error),
        },
      };
    }
    return { data: responseData, paymentInput: data.paymentInput };
  };

  const handleFallbackOnPaymentError = useCallback(
    (error: { code?: string | null } | undefined | null) => {
      if (!error?.code) {
        return;
      }

      const errorConfig = getPaymentErrorConfig(error.code, category === TicketingFormCategory.Auction, t);
      if (!errorConfig) {
        return;
      }

      enqueueSnackbar(errorConfig.message, { vibe: "danger" });
      if (errorConfig.fallbackType === "fullTicketing") {
        fallBackToTicketingContainerOnFullTicketing(setValue as UseFormSetValue<TicketingPaymentInput>);
      } else {
        fallBackToTicketingContainerOnNoTicketSelected(setValue as UseFormSetValue<TicketingPaymentInput>);
      }
    },
    [
      category,
      enqueueSnackbar,
      fallBackToTicketingContainerOnFullTicketing,
      fallBackToTicketingContainerOnNoTicketSelected,
      setValue,
      t,
    ]
  );

  return { handleSubmitCommand };
};

type ErrorConfig = {
  message: string;
  fallbackType: "fullTicketing" | "noTicketSelected";
};

const getPaymentErrorConfig = (
  code: string,
  isAuction: boolean,
  t: ReturnType<typeof useTranslate>["t"]
): ErrorConfig | null => {
  const errorConfigs: Record<string, ErrorConfig> = {
    ticketing_is_full: { message: t("ticketing", "errorOnFullTicketing"), fallbackType: "fullTicketing" },
    ticketing_is_closed: {
      message: isAuction ? t("ticketing", "errorOnClosedAuction") : t("ticketing", "errorOnClosedOccurrence"),
      fallbackType: "noTicketSelected",
    },
    full_choice_errors: { message: t("ticketing", "errorOnFullChoice"), fallbackType: "noTicketSelected" },
    expired_early_bird: { message: t("ticketing", "errorOnExpiredEarlyBirdRate"), fallbackType: "noTicketSelected" },
    rate_sales_not_started: {
      message: t("ticketing", "errorOnRateSalesNotStarted"),
      fallbackType: "noTicketSelected",
    },
    archived_rate_selected: {
      message: t("ticketing", "errorOnArchivedRateSelected"),
      fallbackType: "noTicketSelected",
    },
    hidden_rate_selected: {
      message: t("common", "error.hidden_rate_selected"),
      fallbackType: "noTicketSelected",
    },
    form_deleted: { message: t("ticketing", "errorOnFormDeleted"), fallbackType: "noTicketSelected" },
    discount_usage_exceeds_limit: {
      message: t("common", "error.discount_usage_exceeds_limit"),
      fallbackType: "noTicketSelected",
    },
  };

  return errorConfigs[code] || null;
};

type GetErrorDisplayMessageParams = {
  error: { code?: string | null; message?: string | null } | null | undefined;
  t: ReturnType<typeof useTranslate>["t"];
};

const getErrorDisplayMessage = ({ error, t }: GetErrorDisplayMessageParams): string => {
  const code = error?.code;
  const unknownGenericErrorTraduction = t("common", "unknown_error", {
    code: unknownPaymentErrorCode.other3.code,
  });

  // Stripe error - use message directly
  if (code === "confirm_payment_intent_failed" && error?.message) {
    return error.message;
  }

  // US bank account attach failed - specific message
  if (
    code === "attach_stripe_payment_method_failed" &&
    error?.message ===
      "PaymentMethods of type `us_bank_account` must be verified before they can be attached to a customer."
  ) {
    return t("common", "error.us_bank_account_attach_failed");
  }

  // Try to use code translation
  if (code) {
    const codeTranslationKey = `error.${code}`;
    const translation = t("common", `${codeTranslationKey as never}`);
    if (translation !== codeTranslationKey) {
      return translation;
    }
  }

  return unknownGenericErrorTraduction;
};
